import { defineStore } from 'pinia';
import { useBaseStore } from '../shared/baseStore';
import { container } from 'inversify-props';
import { IStatisticsService, IStatisticsServiceId } from '@/features/statistics/IStatisticsService';
import { FormatDateISO } from '@/helpers/DateFormatHelpers';
import type { MpcStatisticsByDayLineDepartureRow } from './MpcStatisticsByDayLineDepartureRow';

function currentYearMonth(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export const useStatisticsStore = defineStore('statistics', {
    state: () => ({
        mpcStatistics: new Array<any>(),
        currentDate: FormatDateISO(new Date()),
        byDayLineDeparture: null as MpcStatisticsByDayLineDepartureRow[] | null,
        monthlyPeriod: { from: currentYearMonth(), to: currentYearMonth() }
    }),
    actions: {
        setMpcStatistics(payload: any[]) {
            if (!payload) {
                this.mpcStatistics = null;
                return;
            }
            this.mpcStatistics = payload.map((x: any) => {
                x.companyName = x.companyName + '(' + x.companyNumber + ')';
                return x;
            });
        },
        async fetchMpcStatistics(backgroundSync: boolean) {
            const baseStore = useBaseStore();
            const mpcStatisticsService = container.get<IStatisticsService>(IStatisticsServiceId);

            try {
                if (!backgroundSync) {
                    this.setMpcStatistics(new Array<any>());
                    baseStore.setIsLoading(true);
                }

                const currentDate = this.currentDate;
                const mpcStatistics = await mpcStatisticsService.getMpcStatistics(currentDate);

                this.setMpcStatistics(mpcStatistics.data.mpcStatistics);

                if (!backgroundSync) {
                    baseStore.setIsLoading(false);
                }
            } catch (e) {
                console.error(e);
                baseStore.setIsLoading(false);
            }
        },
        async fetchByDayLineDeparture(from: string, to: string) {
            const baseStore = useBaseStore();
            const service = container.get<IStatisticsService>(IStatisticsServiceId);

            try {
                this.byDayLineDeparture = null;
                baseStore.setIsLoading(true);

                this.monthlyPeriod = { from, to };
                this.byDayLineDeparture = await service.getMpcStatisticsByDayLineDeparture(from + '-01', toLastDay(to));
            } catch (e) {
                console.error(e);
                this.byDayLineDeparture = [];
            } finally {
                baseStore.setIsLoading(false);
            }
        }
    }
});

function toLastDay(yearMonth: string): string {
    const [y, m] = yearMonth.split('-').map(Number);
    const date = new Date(y, m, 0); // day 0 of next month = last day of this month
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
